import { supabase } from '../lib/supabase';
import { MarketingLead, Contract, Deal, Owner } from '../types';
import { storage } from './storage';

export interface EmailAttachment {
    filename: string;
    content: string; // Base64 string
    type: string; // MIME type (e.g., 'application/pdf')
}

// Helper pour récupérer l'email noreply depuis la config
const getNoreplyEmail = async (): Promise<string> => {
    try {
        const config = await storage.getSystemConfig('email_noreply');
        if (config) {
            // La config peut être un objet JSON avec une propriété "address"
            if (typeof config === 'object' && config !== null && 'address' in config) {
                const address = (config as { address?: string }).address;
                if (address && typeof address === 'string') {
                    return address;
                }
            }
            // Ou directement une string
            if (typeof config === 'string') {
                return config;
            }
        }
    } catch (error) {
        console.warn('Error fetching email_noreply config, using fallback', error);
    }
    // @ts-ignore - Vite injecte VITE_APP_EMAIL dans import.meta.env
    return import.meta.env.VITE_APP_EMAIL || 'noreply@agences-placid.com';
};

// Helper pour récupérer les emails de tous les admin/superadmin
const getAdminEmails = async (): Promise<string[]> => {
    try {
        const profiles = await storage.getAllProfiles();
        const adminEmails = profiles
            .filter(profile => (profile.role === 'admin' || profile.role === 'superadmin') && profile.email)
            .map(profile => profile.email)
            .filter((email): email is string => !!email);
        return adminEmails;
    } catch (error) {
        console.warn('Error fetching admin emails, continuing without CC', error);
        return [];
    }
};

// Helper pour récupérer les emails des destinataires configurés pour les notifications marketing
// Si aucune config n'existe ou si la liste est vide, retourne tous les admins (rétrocompatibilité)
const getMarketingNotificationEmails = async (): Promise<string[]> => {
    try {
        const config = await storage.getSystemConfig('marketing_notifications_recipients');
        
        // Si pas de config ou liste vide, retourner tous les admins (comportement par défaut)
        if (!config || !config.userIds || config.userIds.length === 0) {
            return await getAdminEmails();
        }
        
        // Récupérer les profils des utilisateurs configurés
        const profiles = await storage.getAllProfiles();
        const configuredEmails = profiles
            .filter(profile => config.userIds.includes(profile.id) && profile.email)
            .map(profile => profile.email)
            .filter((email): email is string => !!email);
        
        // Si aucun email trouvé (utilisateurs supprimés), retourner tous les admins par sécurité
        if (configuredEmails.length === 0) {
            console.warn('No valid emails found for configured marketing notification recipients, falling back to all admins');
            return await getAdminEmails();
        }
        
        return configuredEmails;
    } catch (error) {
        console.warn('Error fetching marketing notification emails, falling back to all admins', error);
        // En cas d'erreur, retourner tous les admins par sécurité
        return await getAdminEmails();
    }
};

// Helper pour wrapper le contenu avec le header/footer Placid
const wrapEmailTemplate = (content: string): string => {
    return `<!DOCTYPE html>
<html>
<head>
<meta charset="utf-8">
<style>
body { font-family: 'Helvetica Neue', Helvetica, Arial, sans-serif; background-color: #f8f9fa; margin: 0; padding: 0; }
.container { max-width: 600px; margin: 0 auto; background-color: #ffffff; border-radius: 8px; overflow: hidden; margin-top: 40px; box-shadow: 0 4px 6px rgba(0,0,0,0.05); }
.header { background-color: #ffffff; padding: 30px; text-align: center; border-bottom: 1px solid #f0f0f0; }
.content { padding: 40px 30px; color: #334155; line-height: 1.6; }
.button { display: inline-block; background-color: #f59e0b; color: #ffffff; text-decoration: none; padding: 12px 24px; border-radius: 6px; font-weight: bold; margin-top: 20px; }
.footer { background-color: #0f172a; padding: 20px; text-align: center; color: #94a3b8; font-size: 12px; }
.info-row { margin: 10px 0; }
.label { font-weight: bold; color: #666; }
.value { color: #333; }
.success-box { background-color: #d4edda; padding: 15px; border-radius: 5px; margin-top: 15px; border-left: 4px solid #28a745; }
.warning-box { background-color: #fff3cd; padding: 15px; border-radius: 5px; margin-top: 15px; border-left: 4px solid #ffc107; }
.appointment { background-color: #fff3cd; padding: 15px; border-radius: 5px; margin-top: 15px; border-left: 4px solid #ffc107; }
</style>
</head>
<body>
<div class="container">
<div class="header">
<img src="https://kfrfvqzjxnadselwusci.supabase.co/storage/v1/object/public/app-assets/logo.png" alt="PLACID" height="30">
</div>
<div class="content">
${content}
</div>
<div class="footer">
© Placid Manager<br>
Cet email est automatique, merci de ne pas y répondre.
</div>
</div>
</body>
</html>`;
};

const sendEmail = async (from: string, to: string, cc: string | string[] | undefined, subject: string, htmlContent: string, replyTo?: string, attachments?: EmailAttachment[]) => {
    console.group("📧 Service Email");
    console.log(`Envoi via Supabase Edge Function 'send-email'`);
    
    // Normaliser cc en tableau : si c'est une chaîne, la splitter, sinon utiliser le tableau directement
    const ccArray = cc ? (Array.isArray(cc) ? cc : cc.split(',').map(e => e.trim()).filter(e => e)) : undefined;
    
    console.log("📧 Paramètres email:", {
        from,
        to,
        cc: ccArray || 'none',
        ccCount: ccArray?.length || 0,
        subject,
        hasHtml: !!htmlContent,
        htmlLength: htmlContent?.length,
        replyTo: replyTo || from
    });
    
    try {
        // Récupérer la session pour obtenir le JWT
        const { data: sessionData } = await supabase.auth.getSession();
        const accessToken = sessionData?.session?.access_token;
        
        console.log("📧 Session check:", {
            hasSession: !!sessionData?.session,
            hasAccessToken: !!accessToken,
            userId: sessionData?.session?.user?.id
        });
        
        // Préparer les headers avec le JWT si disponible
        const headers: Record<string, string> = {};
        if (accessToken) {
            headers['Authorization'] = `Bearer ${accessToken}`;
        }
        
        // Appel sécurisé à la fonction backend (Edge Function)
        const { data, error } = await supabase.functions.invoke('send-email', {
            body: {
                from,
                to: [to],
                cc: ccArray,
                subject,
                html: htmlContent,
                reply_to: replyTo || from,
                attachments: attachments
            },
            headers: Object.keys(headers).length > 0 ? headers : undefined
        });

        if (error) {
            console.error("Erreur Supabase Function:", error);
            
            if (error.code === 'FUNCTION_NOT_FOUND' || error.status === 404) {
                 throw new Error("Le service backend 'send-email' n'est pas déployé sur Supabase. Veuillez contacter l'administrateur.");
            }

            throw new Error(error.message || "Erreur lors de l'appel au service d'email");
        }

        console.log("✅ Email envoyé avec succès (via Backend)", data);
        console.groupEnd();
        return data;

    } catch (e: any) {
        console.error("Erreur critique service email:", e);
        console.groupEnd();
        throw e;
    }
};

export const emailService = {
    sendProposal: async (from: string, to: string, cc: string | string[] | undefined, subject: string, htmlContent: string, replyTo?: string, attachments?: EmailAttachment[]) => {
        return sendEmail(from, to, cc, subject, htmlContent, replyTo, attachments);
    },

    sendLeadTransferNotification: async (agencyEmail: string, lead: MarketingLead, providerName: string, pipelineStage: string, hasAppointment?: boolean, appointmentDate?: string) => {
        const leadSourceLabel = lead.source === 'meta' ? 'Meta (Facebook/Instagram)' : lead.source === 'manual' ? 'Manuel' : lead.source === 'call' ? 'Appel' : 'Autre';
        
        const content = `
            <h2 style="color: #0f172a; margin-top: 0;">📥 Nouveau Lead Transféré</h2>
            <p>Bonjour,</p>
            <p>Un nouveau lead vous a été transféré par <strong>${providerName}</strong>.</p>
            
            <h3>Informations du Lead</h3>
            <div class="info-row">
                <span class="label">Source :</span>
                <span class="value">${leadSourceLabel}</span>
            </div>
            <div class="info-row">
                <span class="label">Contact :</span>
                <span class="value">${lead.firstName || ''} ${lead.lastName || ''}</span>
            </div>
            ${lead.email ? `<div class="info-row"><span class="label">Email :</span><span class="value">${lead.email}</span></div>` : ''}
            ${lead.phone ? `<div class="info-row"><span class="label">Téléphone :</span><span class="value">${lead.phone}</span></div>` : ''}
            
            ${lead.propertyAddress ? `
            <h3>Bien Immobilier</h3>
            <div class="info-row">
                <span class="label">Adresse :</span>
                <span class="value">${lead.propertyAddress}</span>
            </div>
            ${lead.propertyCity ? `<div class="info-row"><span class="label">Ville :</span><span class="value">${lead.propertyCity} ${lead.propertyZipCode || ''}</span></div>` : ''}
            ${lead.propertySurface ? `<div class="info-row"><span class="label">Surface :</span><span class="value">${lead.propertySurface}m²</span></div>` : ''}
            ${lead.propertyRooms ? `<div class="info-row"><span class="label">Pièces :</span><span class="value">${lead.propertyRooms}</span></div>` : ''}
            ` : ''}
            
            <h3>Pipeline</h3>
            <div class="info-row">
                <span class="label">Stage :</span>
                <span class="value">${pipelineStage}</span>
            </div>
            
            ${hasAppointment && appointmentDate ? `
            <div class="appointment">
                <strong>📅 Rendez-vous prévu</strong><br>
                Date : ${new Date(appointmentDate).toLocaleDateString('fr-FR', { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric', hour: '2-digit', minute: '2-digit' })}
            </div>
            ` : ''}
            
            <p style="margin-top: 30px;">
                Vous pouvez consulter ce lead dans votre espace de gestion.
            </p>
        `;

        const htmlContent = wrapEmailTemplate(content);
        const fromEmail = await getNoreplyEmail();
        const formattedFrom = `Placid Manager <${fromEmail}>`;
        const adminEmails = await getMarketingNotificationEmails();
        const ccEmails = adminEmails.length > 0 ? adminEmails : undefined;
        
        return sendEmail(
            formattedFrom,
            agencyEmail,
            ccEmails,
            `Nouveau lead transféré - ${lead.firstName || ''} ${lead.lastName || ''}`,
            htmlContent,
            formattedFrom
        );
    },

    sendContractSignedNotification: async (providerEmail: string, contract: Contract, deal: Deal, owner: Owner, agencyName: string) => {
        const content = `
            <h2 style="color: #0f172a; margin-top: 0;">🎉 Contrat Signé !</h2>
            <p>Bonjour,</p>
            <p>Nous avons le plaisir de vous informer qu'un contrat a été signé à partir d'un lead que vous avez apporté.</p>
            
            <div class="success-box">
                <strong>Félicitations !</strong> Votre lead a abouti à une signature de contrat.
            </div>
            
            <h3>Informations du Contrat</h3>
            ${contract.contractNumber ? `<div class="info-row"><span class="label">Numéro de contrat :</span><span class="value">${contract.contractNumber}</span></div>` : ''}
            <div class="info-row">
                <span class="label">Type :</span>
                <span class="value">${contract.type}</span>
            </div>
            <div class="info-row">
                <span class="label">Date de signature :</span>
                <span class="value">${contract.signedAt ? new Date(contract.signedAt).toLocaleDateString('fr-FR') : 'N/A'}</span>
            </div>
            
            <h3>Propriétaire</h3>
            <div class="info-row">
                <span class="label">Nom :</span>
                <span class="value">${owner.firstName} ${owner.lastName}</span>
            </div>
            ${owner.email ? `<div class="info-row"><span class="label">Email :</span><span class="value">${owner.email}</span></div>` : ''}
            ${owner.phone ? `<div class="info-row"><span class="label">Téléphone :</span><span class="value">${owner.phone}</span></div>` : ''}
            
            <h3>Agence</h3>
            <div class="info-row">
                <span class="label">Agence :</span>
                <span class="value">${agencyName}</span>
            </div>
            
            <p style="margin-top: 30px;">
                Merci pour votre contribution à notre réseau !
            </p>
        `;

        const htmlContent = wrapEmailTemplate(content);
        const fromEmail = await getNoreplyEmail();
        const formattedFrom = `Placid Manager <${fromEmail}>`;
        const adminEmails = await getMarketingNotificationEmails();
        const ccEmails = adminEmails.length > 0 ? adminEmails : undefined;
        
        return sendEmail(
            formattedFrom,
            providerEmail,
            ccEmails,
            `Contrat signé - ${owner.firstName} ${owner.lastName}`,
            htmlContent,
            formattedFrom
        );
    },

    sendLeadAssignmentNotification: async (
        providerEmail: string,
        lead: MarketingLead,
        assignedToUserName: string | null,
        assignmentSuccess: boolean
    ) => {
        const leadSourceLabel = lead.source === 'meta' ? 'Meta (Facebook/Instagram)' : lead.source === 'manual' ? 'Manuel' : lead.source === 'call' ? 'Appel' : 'Autre';
        
        const subject = assignmentSuccess
            ? `Nouveau lead Meta affecté automatiquement - ${lead.firstName || ''} ${lead.lastName || ''}`
            : `Nouveau lead Meta - Affectation manuelle requise - ${lead.firstName || ''} ${lead.lastName || ''}`;
        
        const assignmentInfo = assignmentSuccess
            ? `<div class="success-box">
                <strong>✅ Affectation réussie</strong><br>
                Le lead a été automatiquement affecté à <strong>${assignedToUserName || 'un setter'}</strong>.
            </div>`
            : `<div class="warning-box">
                <strong>⚠️ Affectation automatique impossible</strong><br>
                Le lead n'a pas pu être affecté automatiquement. Veuillez l'affecter manuellement via le superviseur.
            </div>`;
        
        const content = `
            <h2 style="color: #0f172a; margin-top: 0;">${assignmentSuccess ? '✅ Lead Affecté' : '⚠️ Lead Non Affecté'}</h2>
            <p>Bonjour,</p>
            <p>Un nouveau lead Meta a été reçu et ${assignmentSuccess ? 'affecté automatiquement' : 'n\'a pas pu être affecté automatiquement'}.</p>
            
            ${assignmentInfo}
            
            <h3>Informations du Lead</h3>
            <div class="info-row">
                <span class="label">Source :</span>
                <span class="value">${leadSourceLabel}</span>
            </div>
            <div class="info-row">
                <span class="label">Contact :</span>
                <span class="value">${lead.firstName || ''} ${lead.lastName || ''}</span>
            </div>
            ${lead.email ? `<div class="info-row"><span class="label">Email :</span><span class="value">${lead.email}</span></div>` : ''}
            ${lead.phone ? `<div class="info-row"><span class="label">Téléphone :</span><span class="value">${lead.phone}</span></div>` : ''}
            
            ${lead.propertyAddress ? `
            <h3>Bien Immobilier</h3>
            <div class="info-row">
                <span class="label">Adresse :</span>
                <span class="value">${lead.propertyAddress}</span>
            </div>
            ${lead.propertyCity ? `<div class="info-row"><span class="label">Ville :</span><span class="value">${lead.propertyCity} ${lead.propertyZipCode || ''}</span></div>` : ''}
            ${lead.propertyZipCode ? `<div class="info-row"><span class="label">Code postal :</span><span class="value">${lead.propertyZipCode}</span></div>` : ''}
            ${lead.propertySurface ? `<div class="info-row"><span class="label">Surface :</span><span class="value">${lead.propertySurface}m²</span></div>` : ''}
            ${lead.propertyRooms ? `<div class="info-row"><span class="label">Pièces :</span><span class="value">${lead.propertyRooms}</span></div>` : ''}
            ${lead.projectType ? `<div class="info-row"><span class="label">Type de projet :</span><span class="value">${lead.projectType}</span></div>` : ''}
            ` : ''}
            
            ${lead.campaignName ? `
            <h3>Campagne</h3>
            <div class="info-row">
                <span class="label">Campagne :</span>
                <span class="value">${lead.campaignName}</span>
            </div>
            ` : ''}
            
            <p style="margin-top: 30px;">
                ${assignmentSuccess 
                    ? 'Le lead est maintenant en cours de qualification par le setter assigné.'
                    : 'Veuillez affecter ce lead manuellement via l\'interface de gestion des leads.'}
            </p>
        `;

        const htmlContent = wrapEmailTemplate(content);
        const fromEmail = await getNoreplyEmail();
        const formattedFrom = `Placid Manager <${fromEmail}>`;
        
        // Construire la liste CC avec les destinataires configurés pour les notifications marketing
        const adminEmails = await getMarketingNotificationEmails();
        const ccEmails = adminEmails.length > 0 ? adminEmails : undefined;
        
        return sendEmail(
            formattedFrom,
            providerEmail,
            ccEmails,
            subject,
            htmlContent,
            formattedFrom
        );
    },

    sendLeadAssignmentToSetterNotification: async (
        setterEmail: string,
        lead: MarketingLead,
        assignmentMethod: 'auto' | 'manual' | 'reassigned',
        assignedByUserName?: string,
        reason?: string
    ) => {
        const leadSourceLabel = lead.source === 'meta' ? 'Meta (Facebook/Instagram)' : lead.source === 'manual' ? 'Manuel' : lead.source === 'call' ? 'Appel' : 'Autre';
        
        const assignmentMethodLabel = assignmentMethod === 'auto' 
            ? 'Affectation automatique' 
            : assignmentMethod === 'reassigned' 
            ? 'Réaffectation' 
            : 'Affectation manuelle';

        const assignmentInfo = assignmentMethod === 'reassigned' && assignedByUserName
            ? `<p>Ce lead vous a été <strong>réaffecté</strong> par <strong>${assignedByUserName}</strong>${reason ? ` pour la raison suivante : "${reason}"` : ''}.</p>`
            : assignmentMethod === 'manual' && assignedByUserName
            ? `<p>Ce lead vous a été <strong>affecté manuellement</strong> par <strong>${assignedByUserName}</strong>.</p>`
            : `<p>Ce lead vous a été <strong>affecté automatiquement</strong> en fonction de votre localisation.</p>`;

        const content = `
            <h2 style="color: #0f172a; margin-top: 0;">📋 Nouveau Lead Assigné</h2>
            <p>Bonjour,</p>
            <p>Un nouveau lead vous a été assigné (${assignmentMethodLabel}).</p>
            
            ${assignmentInfo}
            
            <h3>Informations du Lead</h3>
            <div class="info-row">
                <span class="label">Source :</span>
                <span class="value">${leadSourceLabel}</span>
            </div>
            <div class="info-row">
                <span class="label">Contact :</span>
                <span class="value">${lead.firstName || ''} ${lead.lastName || ''}</span>
            </div>
            ${lead.email ? `<div class="info-row"><span class="label">Email :</span><span class="value">${lead.email}</span></div>` : ''}
            ${lead.phone ? `<div class="info-row"><span class="label">Téléphone :</span><span class="value">${lead.phone}</span></div>` : ''}
            
            ${lead.propertyAddress ? `
            <h3>Bien Immobilier</h3>
            <div class="info-row">
                <span class="label">Adresse :</span>
                <span class="value">${lead.propertyAddress}</span>
            </div>
            ${lead.propertyCity ? `<div class="info-row"><span class="label">Ville :</span><span class="value">${lead.propertyCity} ${lead.propertyZipCode || ''}</span></div>` : ''}
            ${lead.propertySurface ? `<div class="info-row"><span class="label">Surface :</span><span class="value">${lead.propertySurface}m²</span></div>` : ''}
            ${lead.propertyRooms ? `<div class="info-row"><span class="label">Pièces :</span><span class="value">${lead.propertyRooms}</span></div>` : ''}
            ${lead.projectType ? `<div class="info-row"><span class="label">Type de projet :</span><span class="value">${lead.projectType}</span></div>` : ''}
            ` : ''}
            
            ${lead.campaignName ? `
            <h3>Campagne</h3>
            <div class="info-row">
                <span class="label">Campagne :</span>
                <span class="value">${lead.campaignName}</span>
            </div>
            ` : ''}
            
            <p style="margin-top: 30px;">
                Veuillez qualifier ce lead dans les plus brefs délais via votre espace de gestion.
            </p>
        `;

        const htmlContent = wrapEmailTemplate(content);
        const fromEmail = await getNoreplyEmail();
        const formattedFrom = `Placid Manager <${fromEmail}>`;
        const adminEmails = await getMarketingNotificationEmails();
        const ccEmails = adminEmails.length > 0 ? adminEmails : undefined;
        
        const subject = assignmentMethod === 'reassigned' 
            ? `Lead réaffecté - ${lead.firstName || ''} ${lead.lastName || ''}`
            : `Nouveau lead assigné - ${lead.firstName || ''} ${lead.lastName || ''}`;
        
        return sendEmail(
            formattedFrom,
            setterEmail,
            ccEmails,
            subject,
            htmlContent,
            formattedFrom
        );
    }
};